// WR-04: shared fallback IANA timezone identifier used when an address record
// is missing its `time_zone` field (or when the dev seed addresses do not
// carry one). Previously hardcoded as the literal 'America/New_York' in nine
// places across the schedule and appointment surfaces; centralising means a
// future timezone-correctness audit (or a switch to tenantStore.timezone
// or 'UTC') has exactly one location to update. Per memory
// `project_tenant_tz_is_display_zone.md`, tenant.timezone IS the display
// zone, so the typical production path always has a real value and never
// reads this constant.
export const DEFAULT_TIMEZONE = 'America/New_York';

export function formatInTZ(
  date: string | Date,
  timeZone: string,
  options: Intl.DateTimeFormatOptions,
): string {
  // PODZ-1775: a date-only string (YYYY-MM-DD) carries no time and no zone. It
  // is a civil date (e.g. a progress-billing draw or installment due date) and
  // must render verbatim. new Date('2026-05-28') parses as UTC midnight, and
  // re-projecting that instant into a tenant tz applies a zone a second time,
  // rolling a behind-UTC zone back one calendar day (May 28 became May 27 in
  // America/New_York). Anchor the three civil-date parts at UTC midnight and
  // format in UTC, ignoring the passed timeZone, so a zoneless civil date is
  // never zone-shifted. Strings with a time component (e.g.
  // '2026-05-28T00:00:00Z') do not match this guard and fall through to the
  // tenant-tz path below unchanged.
  // Precondition: this branch keys purely on input shape, so date-only inputs
  // are intended for date-shaped options and render the verbatim civil date in
  // UTC. Pairing a date-only input with time-only options (e.g. { hour:
  // 'numeric' }) is unsupported and would render a meaningless '12:00 AM'.
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [year, month, day] = date.split('-').map(Number);
    const civil = new Date(Date.UTC(year, month - 1, day));
    // Preserve the degrade guard. Date.UTC normalizes overflow (month 13 rolls
    // into the next year, day 99 into a later month) instead of producing an
    // Invalid Date, so a Number.isNaN check alone would silently accept a
    // garbage date like '2026-13-99'. Round-trip the parts back through the
    // constructed Date and degrade to '' if they do not match, matching the
    // existing malformed-input contract that callers v-if on.
    if (
      Number.isNaN(civil.getTime()) ||
      civil.getUTCFullYear() !== year ||
      civil.getUTCMonth() !== month - 1 ||
      civil.getUTCDate() !== day
    ) {
      return '';
    }
    return new Intl.DateTimeFormat('en-US', {
      ...options,
      timeZone: 'UTC',
    }).format(civil);
  }
  const d = new Date(date);
  // WR-03: a present-but-garbage date string (e.g. a backend serialization
  // regression) parses to Invalid Date, and Intl.format(Invalid Date) throws
  // RangeError, blanking the whole card. Degrade to '' so callers that already
  // v-if on the result simply omit it.
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', { timeZone, ...options }).format(d);
}

export function formatArrivalWindow(
  start: string,
  end: string,
  timeZone: string,
): string {
  const opts: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
  };
  const startStr = formatInTZ(start, timeZone, opts);
  const endStr = formatInTZ(end, timeZone, opts);
  return `${startStr} – ${endStr}`;
}

// WR-03: shared service-duration formatter so WizardStepCategory and
// WizardStepConfirm emit identical labels for the same service. The compact
// "2h" / "2h 30m" / "30m" form works at both badge/chip sizes and in the
// category-list duration tag. Returns an empty string for non-positive or
// non-numeric input so callers can use v-if directly on the result.
export function formatServiceDuration(
  minutes: number | null | undefined,
): string {
  if (
    typeof minutes !== 'number' ||
    !Number.isFinite(minutes) ||
    minutes <= 0
  ) {
    return '';
  }
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder ? `${hours}h ${remainder}m` : `${hours}h`;
}
